/**
 * Auth Context
 * Provides authentication state and methods throughout the application
 */
import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/auth/authService';

export const AuthContext = createContext(null);

/**
 * Auth Provider Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Auth provider
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from storage
  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getToken();
      const storedUser = authService.getStoredUser();

      if (token && storedUser) {
        try {
          // Verify token is still valid by fetching current user
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          setIsAuthenticated(true);
        } catch (error) {
          // Token is invalid, clear auth data
          authService.clearAuthData();
          setUser(null);
          setIsAuthenticated(false);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Login user
   * @param {Object} credentials - Login credentials
   * @returns {Promise<Object>} Login response
   */
  const login = useCallback(async (credentials) => {
    const response = await authService.login(credentials);
    setUser(response.user);
    setIsAuthenticated(true);
    return response;
  }, []);

  /**
   * Register new user
   * @param {Object} userData - Registration data
   * @returns {Promise<Object>} Registration response
   */
  const register = useCallback(async (userData) => {
    const response = await authService.register(userData);
    return response;
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  /**
   * Update user profile
   * @param {Object} profileData - Profile update data
   * @returns {Promise<Object>} Updated user
   */
  const updateProfile = useCallback(async (profileData) => {
    const response = await authService.updateProfile(profileData);
    setUser(response.user);
    return response.user;
  }, []);

  /**
   * Change password
   * @param {Object} passwordData - Password change data
   * @returns {Promise<Object>} Response
   */
  const changePassword = useCallback(async (passwordData) => {
    const response = await authService.changePassword(passwordData);
    return response;
  }, []);

  /**
   * Update user state (e.g., after role change)
   * @param {Object} updatedUser - Updated user object
   */
  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    authService.updateStoredUser(updatedUser);
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
